import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import Button from '../../../components/button'
import FormFileInput from '../../../components/form-file-input'
import SalaryReviewLayout from '../../../components/layouts/salary-review'
import { useRouteQuery } from '../../../hooks/useRouteQuery'
import { useSalaryReviewsBulk } from '../../../redux/employees/hook'
import { formValidation } from '../../../utils/functions'
import Excel from 'exceljs'
import FormSwitch from '../../../components/form-switch'
import { useSelector } from 'react-redux'

const typeMap = {
  'net-pay': 'net_pay',
  'gross-pay': 'gross_pay',
  'fill-info': 'complete_pay',
}

function ReviewBulkEmployeePay() {
  const {
    register,
    handleSubmit,
    errors,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
  })

  useEffect(() => {}, [])

  const history = useHistory()
  const query = useRouteQuery()

  const [payType, setPayType] = useState('')
  const { section } = useSelector((state) => state.sectionSlice)

  const { mutateAsync: salaryReviewsBulk, isLoading: reviewLoading } =
    useSalaryReviewsBulk()
  const [sendEmail, setSendEmail] = useState(false)

  useEffect(() => {
    if (query) {
      setPayType(query.get('type'))
    }
  }, [query])

  const submitForm = async (data) => {
    const formData = new FormData()
    formData.append('file', data?.file[0])
    formData.append('type', typeMap[payType])
    formData.append('send_mail', sendEmail ? 1 : 0)

    await salaryReviewsBulk(formData).then((result) => {
      history.push('/employees')
    })
  }

  const netPayColumns = [
    { header: 'First Name', key: 'first_name' },
    { header: 'Last Name', key: 'last_name' },
    { header: 'Phone', key: 'phone' },
    { header: 'Review Date (YYYY-MM-DD)', key: 'review_date' },
    { header: 'New Net Pay', key: 'new_net_pay' },
  ]

  const grossPayColumns = [
    { header: 'First Name', key: 'first_name' },
    { header: 'Last Name', key: 'last_name' },
    { header: 'Phone', key: 'phone' },
    { header: 'Review Date (YYYY-MM-DD)', key: 'review_date' },
    { header: 'New Gross Pay', key: 'new_gross_pay' },
  ]

  const fullPayColumns = [
    { header: 'First Name', key: 'first_name' },
    { header: 'Last Name', key: 'last_name' },
    { header: 'Phone', key: 'phone' },
    { header: 'Review Date (YYYY-MM-DD)', key: 'review_date' },
    { header: 'New Gross Pay', key: 'new_gross_pay' },
    { header: 'New Basic', key: 'new_basic' },
    { header: 'New Housing', key: 'new_housing' },
    { header: 'New Transport', key: 'new_transport' },
    { header: 'New Leave', key: 'new_leave' },
    { header: 'New Lunch', key: 'new_lunch' },
    { header: 'New Utility', key: 'new_utility' },
    { header: 'New Benefit In Kind', key: 'new_benefit_in_kind' },
    { header: 'New Other Allowance', key: 'new_other_allowance' },
  ]

  const [exportLoading, setSetExportLoading] = useState(false)
  const workbook = new Excel.Workbook()

  const exportTemplate = async () => {
    setSetExportLoading(true)
    const workSheetName = 'salaries'
    const workBookName = `Employee Salary Review`
    try {
      const fileName = workBookName

      const worksheet = workbook.addWorksheet(workSheetName)

      // add worksheet columns
      // each columns contains header and its mapping key from data
      worksheet.columns =
        payType == 'net-pay'
          ? netPayColumns
          : payType == 'gross-pay'
          ? grossPayColumns
          : fullPayColumns

      worksheet.columns.forEach((column) => {
        if (column._number == 4) {
          column.width = column.header.length
          column.numFmt = 'YYYY-MM-DD'
        } else if (column._number > 4) {
          column.width = 25
          column.numFmt = '0.00'
        } else {
          column.width = 25
        }
      })

      // updated the font for first row.
      worksheet.getRow(1).font = { bold: true }
      // loop through all of the rows and set the outline style.
      worksheet.eachRow({ includeEmpty: true }, (row) => {
        // store each cell to currentCell
        const currentCell = row._cells

        // loop through currentCell to apply border only for the non-empty cell of excel
        currentCell.forEach((singleCell) => {
          // store the cell address i.e. A1, A2, A3, B1, B2, B3, ...
          const cellAddress = singleCell._address
          worksheet.getCell(cellAddress).border = {
            top: { style: 'none' },
            left: { style: 'none' },
            bottom: { style: 'none' },
            right: { style: 'none' },
          }
        })
      })

      // write the content using writeBuffer
      const buf = await workbook.xlsx.writeBuffer()

      // download the processed file
      saveAs(new Blob([buf]), `${fileName}.xlsx`)
    } catch (error) {
      // console.error('<<<ERRROR>>>', error);
      // console.error('Something Went Wrong', error.message);
    } finally {
      workbook.removeWorksheet(workSheetName)
    }
    setSetExportLoading(false)
  }

  return (
    <SalaryReviewLayout step={2} title={'A Compensation'}>
      <div className='flex flex-col w-full h-fit'>
        <span className='header-4 mb-3'>
          {payType === 'net-pay'
            ? 'I know the New Net Pay only'
            : payType === 'gross-pay'
            ? 'I know the New Gross Pay only'
            : 'I Know The New Complete Compensation'}
        </span>
        <form className='form ' onSubmit={handleSubmit(submitForm)}>
          <div className='p-4 border border-secondary-2-extralight rounded-md h-fit flex flex-col px-[60px]'>
            <div className='flex flex-col text-center justify-center items-center'>
              <span className='header-4-medium'>
                Upload employees information to review salaries
              </span>
              <Button
                text='Download template'
                type='button'
                theme='primary'
                loading={exportLoading}
                className='flex gap-2 !h-[35px] w-[170px] p-2 mt-3 '
                textClass={'!text-[11px]'}
                onClick={() => {
                  exportTemplate()
                }}
              />

              <FormFileInput
                multiSource={true}
                inputRef={register(formValidation('file', true))}
                accept='.xlsx'
              />
            </div>
            {section.name === 'core hr' && (
              <div className='mt-2 w-full'>
                <FormSwitch
                  label={
                    'Do you want to send salary review email to employees?'
                  }
                  name={'send_email'}
                  checked={sendEmail}
                  onClick={() => setSendEmail(!sendEmail)}
                  value={sendEmail}
                />
              </div>
            )}
          </div>

          <div className='w-full mt-[20px]'>
            <Button
              text={'Review Salaries'}
              type='submit'
              loading={reviewLoading}
            />
          </div>
          <div
            className='flex justify-center w-full cursor-pointer'
            onClick={() => history.goBack()}
          >
            <span className='p2-medium text-color-black mt-3'>Go Back</span>
          </div>
        </form>
      </div>
    </SalaryReviewLayout>
  )
}

export default ReviewBulkEmployeePay
